import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { fromEvent, interval, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { RemmiConfirmService } from '@common/remmi-confirm/remmi-confirm.service';
import { SignupRequest, SignupRequestsService, SignupStatus } from '@services/api/signup-requests.service';

/** Background poll cadence — detect new signup requests without a page reload. */
const POLL_MS = 60 * 1000;
/** Approve responds instantly but the CRM contact syncs in the background — re-fetch after this. */
const CRM_SYNC_RECHECK_MS = 4000;

@Component({
  selector: 'app-signup-requests-popup',
  templateUrl: './signup-requests-popup.component.html',
  styleUrls: ['./signup-requests-popup.component.scss'],
})
export class SignupRequestsPopupComponent implements OnInit, OnDestroy {
  visible = false;
  loading = false;

  /** Active list filter. The popup opens on `pending`; admin can switch tabs. */
  status: SignupStatus = 'pending';
  requests: SignupRequest[] = [];
  /** Count of pending requests — drives the header title, regardless of active tab. */
  pendingCount = 0;

  /** Id of the row with an action in flight — disables that row's buttons. */
  busyId: string | null = null;
  /** Id of the row whose reject-reason panel is open. */
  rejectingId: string | null = null;
  rejectReason = '';

  readonly tabs: { key: SignupStatus; label: string }[] = [
    { key: 'pending', label: 'Pending' },
    { key: 'approved', label: 'Approved' },
    { key: 'rejected', label: 'Rejected' },
  ];

  private destroy$ = new Subject<void>();
  private destroyed = false;

  /** Browser tab is in the background — nothing to show a popup to, so don't poll. */
  private get isHidden(): boolean {
    return typeof document !== 'undefined' && document.visibilityState === 'hidden';
  }

  constructor(
    private signupService: SignupRequestsService,
    private toastr: ToastrService,
    private remmiConfirm: RemmiConfirmService,
    private zone: NgZone
  ) {}

  ngOnInit(): void {
    // On load: open the popup if signup requests already exist.
    this.pollAndMaybeOpen();

    // Background poll: auto-open when a NEW pending request appears — no page reload.
    //
    // Skipped while the tab is hidden. This endpoint costs ~0.6s of backend time per poll for a
    // 180-byte response, and it kept polling long after the user had stopped navigating — with
    // several tabs open that multiplies directly (FRONTEND-DUPLICATE-API-CALLS-2026-09-17.md §5).
    // A hidden tab has nobody to show the popup to, so the poll has no one to serve. `poll()` also
    // runs once on becoming visible again, so a request that arrived while hidden still surfaces
    // immediately rather than waiting out the rest of the interval.
    // Ticks outside the zone so a hidden-tab tick costs no change detection; `poll()` re-enters it.
    this.zone.runOutsideAngular(() =>
      interval(POLL_MS)
        .pipe(
          filter(() => !this.isHidden),
          takeUntil(this.destroy$)
        )
        .subscribe(() => this.zone.run(() => this.poll()))
    );

    fromEvent(document, 'visibilitychange')
      .pipe(
        filter(() => !this.isHidden),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.poll());

    // Open on demand when another part of the app asks (e.g. dashboard link).
    this.signupService.open$.pipe(takeUntil(this.destroy$)).subscribe(() => this.open());
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Open the popup on demand. Shows regardless of pending count, on the pending tab. */
  open(): void {
    this.status = 'pending';
    this.rejectingId = null;
    this.visible = true;
    this.fetch();
  }

  /** Fetch pending requests; auto-open the popup when at least one exists. */
  private pollAndMaybeOpen(): void {
    this.status = 'pending';
    this.fetch(() => {
      if (this.pendingCount > 0) {
        this.visible = true;
      }
    });
  }

  /**
   * Background poll — reads the pending count only, without disturbing the
   * admin's current tab or list. Auto-opens the popup when the count rises
   * (a new request arrived), even if the admin had closed it. A closed popup
   * stays closed while the count is unchanged — re-open it via the dashboard
   * "Agent Portal Approval" link or wait for the next new request.
   */
  private poll(): void {
    this.signupService
      .list('pending')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          const count = res?.data?.pendingCount ?? 0;
          const hasNew = count > this.pendingCount;
          this.pendingCount = count;
          this.signupService.setPendingCount(count);
          if (hasNew) {
            this.open();
          }
        },
        // Silent on error — a background poll must not spam toasts.
        error: () => {},
      });
  }

  setStatus(status: SignupStatus): void {
    if (this.status === status) {
      return;
    }
    this.status = status;
    this.rejectingId = null;
    this.fetch();
  }

  /** Load the list for the current status filter. */
  private fetch(onDone?: () => void): void {
    this.loading = true;
    this.signupService
      .list(this.status)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: res => {
          this.loading = false;
          this.requests = res?.data?.requests ?? [];
          this.pendingCount = res?.data?.pendingCount ?? 0;
          this.signupService.setPendingCount(this.pendingCount);
          onDone?.();
        },
        error: err => {
          this.loading = false;
          this.toastr.error(this.errorMessage(err, 'Could not load signup requests'));
        },
      });
  }

  /** Approve (pending) or retry a failed CRM sync — both call the approve endpoint. */
  approve(request: SignupRequest): void {
    if (this.busyId) {
      return;
    }
    this.busyId = request._id;
    this.signupService
      .approve(request._id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.busyId = null;
          this.toastr.success(`${request.name} approved — CRM contact is syncing.`);
          this.fetch();
          // CRM contact creation runs in the background; re-fetch once to pick up the result.
          setTimeout(() => {
            if (!this.destroyed) {
              this.fetch();
            }
          }, CRM_SYNC_RECHECK_MS);
        },
        error: err => {
          this.busyId = null;
          this.toastr.error(this.errorMessage(err, 'Approve failed'));
        },
      });
  }

  /** Toggle the inline reject-reason panel for a row. */
  toggleReject(request: SignupRequest): void {
    this.rejectingId = this.rejectingId === request._id ? null : request._id;
    this.rejectReason = '';
  }

  /** Submit the reject — reason is optional. */
  confirmReject(request: SignupRequest): void {
    if (this.busyId) {
      return;
    }
    this.busyId = request._id;
    this.signupService
      .reject(request._id, this.rejectReason.trim() || undefined)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.busyId = null;
          this.rejectingId = null;
          this.toastr.success(`${request.name} rejected.`);
          this.fetch();
        },
        error: err => {
          this.busyId = null;
          this.toastr.error(this.errorMessage(err, 'Reject failed'));
        },
      });
  }

  /** Confirm, then delete a row. Approved requests also delete their Dynamics CRM contact. */
  openDeleteDialog(request: SignupRequest): void {
    if (this.busyId) {
      return;
    }
    const message =
      request?.approvalStatus === 'approved'
        ? 'This signup request and its Dynamics CRM contact will be permanently deleted.'
        : 'This signup request will be permanently deleted.';
    this.remmiConfirm
      .delete({ title: 'Delete Signup Request?', message, subject: request?.name })
      .subscribe(isConfirmed => isConfirmed && this.deleteRequest(request));
  }

  private deleteRequest(request: SignupRequest): void {
    if (!request || this.busyId) {
      return;
    }
    this.busyId = request._id;
    this.signupService
      .delete(request._id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.busyId = null;
          this.toastr.success('Signup request deleted.');
          this.fetch();
        },
        error: err => {
          this.busyId = null;
          // HTTP 409: CRM contact creation still in progress — the admin can retry the delete.
          this.toastr.error(this.errorMessage(err, 'Delete failed'));
        },
      });
  }

  close(): void {
    this.visible = false;
    this.rejectingId = null;
  }

  /** Show the approve/retry button: on the pending tab, or for any failed CRM sync. */
  canApprove(request: SignupRequest): boolean {
    return this.status === 'pending' || request.crmSyncStatus === 'failed';
  }

  trackById(_: number, request: SignupRequest): string {
    return request._id;
  }

  /** GET errors surface the raw HttpErrorResponse; POST/DELETE surface the parsed body. Handle both. */
  private errorMessage(err: any, fallback: string): string {
    return err?.error?.message || err?.message || fallback;
  }
}
